import * as dotenv from 'dotenv';
import {
  IDeleteFileReturnValue,
  IGetFileReturnValue,
  IUploadFileReturnValue,
} from 'src/model/dto/file-dto';
import { IUploadPictureReturnValue } from 'src/model/dto/picture-dto';
import {
  IDeleteResumeReturnValue,
  IUploadResumeReturnValue,
} from 'src/model/dto/resume-dto';
import {
  IActivateUserDto,
  IDeleteUserDto,
  IUpdateUserDto,
} from 'src/model/dto/user-dto';
import { ILoginResponse, IProject, IProposition, IStep, IUser } from '../model';
import {
  IChangePasswordDto,
  IResetPasswordDto,
  ISignUpDto,
} from '../model/dto/auth-dto';
import { ICreateProjectDto, IUpdateProjectDto } from '../model/dto/project-dto';
import {
  ICreateEvaluationDto,
  ICreatePropositionDto,
  ISendMessageDto,
  IUpdateEvaluationDto,
  IUpdatePropositionDto,
} from '../model/dto/proposition-dto';
import { ICreateStepDto, IUpdateStepDto } from '../model/dto/step-dto';
import { AxiosPromise } from '../utils/axios';

dotenv.config();
const url = process.env.REACT_APP_API_URL;

// Authentication

export const register = async (data: ISignUpDto): Promise<ILoginResponse> => {
  const user = await AxiosPromise('post', `${url}/auth/register`, data);
  if (user) {
    localStorage.setItem('beadvize-accessToken', user.data.accessToken);
    localStorage.setItem('beadvize-accessTokenExpiration', user.data.accessTokenExpiration
    );
  }
  return user.data;
};

export const authenticate = async (
  email: string,
  password: string
): Promise<ILoginResponse> => {
  const data = {
    email,
    password,
  };
  const user = await AxiosPromise('post', `${url}/auth/authenticate`, data);
  if (user) {
    localStorage.setItem('beadvize-accessToken', user.data.accessToken);
    localStorage.setItem('beadvize-accessTokenExpiration', user.data.accessTokenExpiration);
  }
  return user.data;
};

export const resetPasswordToken = async (email: string) => {
  const data = {
    email,
  };
  const request = await AxiosPromise(
    'post',
    `${url}/auth/resetpasswordtoken`,
    data
  );
  return request.data;
};

export const resetPassword = async (data: IResetPasswordDto) => {
  const request = await AxiosPromise('post', `${url}/auth/resetpassword`, data);
  return request.data;
};

export const changePassword = async (data: IChangePasswordDto) => {
  const request = await AxiosPromise(
    'post',
    `${url}/auth/changepassword`,
    data
  );
  return request.data;
};

// Users

export const getMe = async (): Promise<IUser> => {
  const user = await AxiosPromise('get', `${url}/user/me`);
  return user.data;
};

export const getUser = async (userId: string): Promise<IUser> => {
  const user = await AxiosPromise('get', `${url}/user/${userId}`);
  return user.data;
};

export const updateUser = async (data: IUpdateUserDto): Promise<IUser> => {
  const user = await AxiosPromise('patch', `${url}/user/me`, data);
  return user.data;
};

export const sendActivationEmail = async (): Promise<void> => {
  await AxiosPromise('post', `${url}/user/me/sendActivation`);
};

export const activateUser = async (data: IActivateUserDto): Promise<IUser> => {
  const user = await AxiosPromise('post', `${url}/user/me/activate`, data);
  return user.data;
};

export const inactivateUser = async (data: IDeleteUserDto): Promise<IUser> => {
  const user = await AxiosPromise('post', `${url}/user/me/inactivate`, data);
  return user.data;
};

export const deleteUser = async (): Promise<IUser> => {
  const user = await AxiosPromise('post', `${url}/user/me/delete`);
  return user.data;
};

export const uploadResume = async (
  file: any
): Promise<IUploadResumeReturnValue> => {
  const data = new FormData();
  data.append('file', file);
  const uploadedPicture = await AxiosPromise(
    'post',
    `${url}/user/me/resume`,
    data,
    {},
    'multipart/form-data'
  );
  return uploadedPicture.data;
};

export const deleteResume = async (): Promise<IDeleteResumeReturnValue> => {
  const deletedResume = await AxiosPromise('delete', `${url}/user/me/resume`);
  return deletedResume.data;
};

export const uploadProfilePicture = async (
  file: any
): Promise<IUploadPictureReturnValue> => {
  const data = new FormData();
  data.append('file', file);
  const uploadedPicture = await AxiosPromise(
    'post',
    `${url}/user/me/picture`,
    data,
    {},
    'multipart/form-data'
  );
  return uploadedPicture.data;
};

export const deleteProfilePicture = async (): Promise<
  IUploadPictureReturnValue
> => {
  const deletedPicture = await AxiosPromise('delete', `${url}/user/me/picture`);
  return deletedPicture.data;
};

// Projects

export const listMyProjects = async (): Promise<IProject[]> => {
  const projectList = await AxiosPromise('get', `${url}/project/mine`);
  return projectList.data;
};

export const getProject = async (projectId: string): Promise<IProject> => {
  const project = await AxiosPromise('get', `${url}/project/${projectId}`);
  return project.data;
};

export const createProject = async (
  data: ICreateProjectDto
): Promise<IProject> => {
  const project = await AxiosPromise('post', `${url}/project`, data);
  return project.data;
};

export const createHiddenProject = async (): Promise<IProject> => {
  const project = await AxiosPromise('post', `${url}/project/hidden`);
  return project.data;
};

export const updateProject = async (
  projectId: string,
  data: IUpdateProjectDto
): Promise<IProject> => {
  const project = await AxiosPromise(
    'patch',
    `${url}/project/${projectId}`,
    data
  );
  return project.data;
};

export const deleteProject = async (
  projectId: string
): Promise<{
  ok?: number;
  n?: number;
}> => {
  const project = await AxiosPromise('delete', `${url}/project/${projectId}`);
  return project.data;
};

export const startProject = async (projectId: string): Promise<IProject> => {
  const project = await AxiosPromise(
    'post',
    `${url}/project/${projectId}/start`
  );
  return project.data;
};

export const endProject = async (projectId: string): Promise<IProject> => {
  const project = await AxiosPromise('post', `${url}/project/${projectId}/end`);
  return project.data;
};

export const listProjectPropositions = async (
  projectId: string
): Promise<IProposition[]> => {
  const proposition = await AxiosPromise(
    'get',
    `${url}/project/${projectId}/propositions`
  );
  return proposition.data;
};

export const createProposition = async (
  projectId: string,
  data: ICreatePropositionDto
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'post',
    `${url}/project/${projectId}/propositions`,
    data
  );
  return proposition.data;
};

export const getMyProposition = async (
  projectId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'get',
    `${url}/project/${projectId}/propositions/mine`
  );
  return proposition.data;
};

export const uploadProjectFile = async (
  projectId: string,
  file: any
): Promise<IUploadFileReturnValue> => {
  const data = new FormData();
  data.append('file', file);
  const uploadedFile = await AxiosPromise(
    'post',
    `${url}/project/${projectId}/files`,
    data,
    {},
    'multipart/form-data'
  );
  return uploadedFile.data;
};

export const getProjectFileUrl = async (
  projectId: string,
  fileUuid: string
): Promise<IGetFileReturnValue> => {
  const fileUrl = await AxiosPromise(
    'get',
    `${url}/project/${projectId}/files/${fileUuid}`
  );
  return fileUrl.data;
};

export const deleteProjectFile = async (
  projectId: string,
  fileUuid: string
): Promise<IDeleteFileReturnValue> => {
  const deletedFile = await AxiosPromise(
    'delete',
    `${url}/project/${projectId}/files/${fileUuid}`
  );
  return deletedFile.data;
};

// Propositions

export const listMyPropositions = async (): Promise<IProposition[]> => {
  const proposition = await AxiosPromise('get', `${url}/proposition/mine`);
  return proposition.data;
};

export const getProposition = async (
  propositionId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'get',
    `${url}/proposition/${propositionId}`
  );
  return proposition.data;
};

export const getAcceptedProposition = async (
  projectId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'get',
    `${url}/project/${projectId}/propositions?meta.status=accepted`
  );
  return proposition.data[0];
};

export const updateProposition = async (
  propositionId: string,
  data: IUpdatePropositionDto
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'patch',
    `${url}/proposition/${propositionId}`,
    data
  );
  return proposition.data;
};

export const proposeProposition = async (
  propositionId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/propose`
  );
  return proposition.data;
};

export const sendProposition = async (
  propositionId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/send`
  );
  return proposition.data;
};

export const declineProposition = async (
  propositionId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/decline`
  );
  return proposition.data;
};

export const rejectProposition = async (
  propositionId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/reject`
  );
  return proposition.data;
};


export const shortlistProposition = async (
  propositionId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/shortlist`
  );
  return proposition.data;
};

export const acceptProposition = async (
  propositionId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/accept`
  );
  return proposition.data;
};

export const listPropositionSteps = async (
  propositionId: string
): Promise<IStep> => {
  const stepList = await AxiosPromise(
    'get',
    `${url}/proposition/${propositionId}/steps`
  );
  return stepList.data;
};

export const createSteps = async (
  propositionId: string,
  data: ICreateStepDto[]
): Promise<string[]> => {
  const step = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/steps`,
    data
  );
  return step.data;
};

export const deleteSteps = async (
  propositionId: string
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'delete',
    `${url}/proposition/${propositionId}/steps`
  );
  return proposition.data;
};

export const sendMessage = async (
  propositionId: string,
  data: ISendMessageDto
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/messages`,
    data
  );
  return proposition.data;
};

export const createEvaluation = async (
  propositionId: string,
  data: ICreateEvaluationDto
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/evaluations`,
    data
  );
  return proposition.data;
};

export const updateEvaluation = async (
  propositionId: string,
  data: IUpdateEvaluationDto
): Promise<IProposition> => {
  const proposition = await AxiosPromise(
    'patch',
    `${url}/proposition/${propositionId}/evaluations`,
    data
  );
  return proposition.data;
};

export const uploadPropositionFile = async (
  propositionId: string,
  file: any
): Promise<IUploadFileReturnValue> => {
  const data = new FormData();
  data.append('file', file);
  const uploadedFile = await AxiosPromise(
    'post',
    `${url}/proposition/${propositionId}/files`,
    data,
    {},
    'multipart/form-data'
  );
  return uploadedFile.data;
};

export const getPropositionFileUrl = async (
  propositionId: string,
  fileUuid: string
): Promise<IGetFileReturnValue> => {
  const fileUrl = await AxiosPromise(
    'get',
    `${url}/proposition/${propositionId}/files/${fileUuid}`
  );
  return fileUrl.data;
};

export const deletePropositionFile = async (
  propositionId: string,
  fileUuid: string
): Promise<IDeleteFileReturnValue> => {
  const deletedFile = await AxiosPromise(
    'delete',
    `${url}/proposition/${propositionId}/files/${fileUuid}`
  );
  return deletedFile.data;
};

// Steps

export const updateStep = async (
  stepId: string,
  data: IUpdateStepDto
): Promise<IStep> => {
  const step = await AxiosPromise('patch', `${url}/step/${stepId}`, data);
  return step.data;
};

export const startStep = async (stepId: string): Promise<IStep> => {
  const step = await AxiosPromise('post', `${url}/step/${stepId}/start`);
  return step.data;
};

export const validateStep = async (stepId: string): Promise<IStep> => {
  const step = await AxiosPromise('post', `${url}/step/${stepId}/validate`);
  return step.data;
};



// WEBPACK FOOTER //
// ./src/services/index.ts